/**
 * Listados en el celular: la misma tabla, leída como tarjetas.
 *
 * Una tabla de nueve columnas no se puede leer en 390 px: aplastada, el
 * encabezado se pisaba consigo mismo y, desplazándola en horizontal, había que ir
 * y volver para cruzar dos datos de la misma fila. En su lugar cada fila se
 * dibuja como una tarjeta —el dato con el nombre de su columna al lado— y el
 * paginado sigue funcionando igual, porque las filas son las mismas.
 *
 * El dibujo lo hace el CSS (`index.css`, «Listados en el celular»). Lo que el CSS
 * no puede saber es **el nombre de la columna de cada celda**: eso sólo está en el
 * encabezado de su propia tabla. En vez de repetir `data-etiqueta` en las ~100
 * tablas del panel (y olvidarlo en la siguiente), se copia desde aquí: un
 * observador mira lo que se va dibujando y escribe la etiqueta en cada celda.
 *
 * Sólo escribe **atributos** (`data-etiqueta`, `data-titulo`), que el CSS usa como
 * `content`; ningún dato, cálculo ni orden cambia, y en escritorio no se usa nada
 * de esto.
 */

import { useEffect } from 'react';

/** A partir de cuántas columnas merece la pena el formato de tarjetas. */
const MINIMO_COLUMNAS = 5;

let observador: MutationObserver | null = null;
let pendiente = 0;

/** Copia el nombre de cada columna en las celdas de su tabla. */
function etiquetar(tabla: HTMLTableElement): void {
  // Si la tabla tiene dos filas de encabezado, la que nombra las columnas es la
  // última (la primera suele agrupar).
  const filasCabecera = tabla.querySelectorAll('thead tr');
  const cabecera = filasCabecera[filasCabecera.length - 1];

  if (!cabecera || cabecera.children.length < MINIMO_COLUMNAS) return;

  const etiquetas = Array.from(cabecera.children).map((celda) =>
    (celda.textContent ?? '').trim(),
  );

  tabla.querySelectorAll('tbody tr, tfoot tr').forEach((fila) => {
    // Las tablas largas (147 filas en comprobantes) se revisan enteras en cada
    // cambio de React: una fila ya etiquetada no se vuelve a tocar.
    if (fila.hasAttribute('data-etiquetas-listas')) return;

    // El nombre de la columna se busca por **posición**, no por orden en la fila:
    // las filas de totales juntan varias columnas en una celda (`colSpan`) y, si no
    // se contaran, las etiquetas saldrían corridas («Volumen» sobre el importe).
    let columna = 0;

    const celdas = Array.from(fila.children).map((celda) => {
      const ancho = (celda as HTMLTableCellElement).colSpan || 1;
      const etiqueta = etiquetas[columna] ?? '';
      columna += ancho;

      return { celda, etiqueta, ancho };
    });

    // La primera columna con nombre y con algo escrito abre la tarjeta, a modo de
    // título. Se pide texto porque hay tablas que empiezan con una foto.
    const titulo = celdas.find(
      ({ celda, etiqueta, ancho }) =>
        ancho === 1 && etiqueta.length > 0 && (celda.textContent ?? '').trim().length > 0,
    )?.celda;

    celdas.forEach(({ celda, etiqueta, ancho }) => {
      // Las celdas que ocupan varias columnas (avisos de «sin datos», subtotales)
      // no llevan etiqueta: se dibujan como una línea.
      if (ancho > 1 || etiqueta.length === 0) {
        celda.removeAttribute('data-etiqueta');
        celda.removeAttribute('data-titulo');
        return;
      }

      celda.setAttribute('data-etiqueta', etiqueta);

      if (celda === titulo) celda.setAttribute('data-titulo', '');
      else celda.removeAttribute('data-titulo');
    });

    fila.setAttribute('data-etiquetas-listas', '');
  });
}

function revisar(): void {
  document.querySelectorAll('table').forEach((tabla) => {
    etiquetar(tabla as HTMLTableElement);
  });
}

/**
 * Activa el formato de tarjetas: pone el observador una sola vez y etiqueta lo que
 * ya esté dibujado. Se llama desde el armazón (`AppShell`), así que cubre también
 * los diálogos (se dibujan en un portal, fuera de `<main>`).
 */
export function useTablasTarjetas(): void {
  useEffect(() => {
    revisar();

    if (observador) return undefined;

    // Se agrupa con `requestAnimationFrame`: React puede añadir muchos nodos de
    // golpe (una página entera) y no hace falta revisar cada uno por separado.
    observador = new MutationObserver(() => {
      if (pendiente) return;
      pendiente = requestAnimationFrame(() => {
        pendiente = 0;
        revisar();
      });
    });

    observador.observe(document.body, { childList: true, subtree: true });

    return undefined;
  }, []);
}
